/**
 * Pulling records from a connected system into this device — **M2, locally**, per
 * [ADR-074](../../../../docs/platform/11-technical-architecture.md#8--architecture-decision-records).
 *
 * ⚠️ **`ingest()` had 319 lines, a test suite, and no application code calling it.** The same shape
 * as the executor, the sweep and the audit chain before them: a correct engine nothing imported.
 * This module is its first caller, which is what makes M2 a feature rather than a package.
 *
 * ── What a local sync is, and what it deliberately is not ──────────────────────────────────
 *
 * It is a **foreground, human-started, resumable** run. It is **not a schedule** —
 * [11 §6](../../../../docs/platform/11-technical-architecture.md) already states that scheduled and
 * continuous monitoring are unavailable locally *by architecture*, because a browser tab is not a
 * runtime anyone may promise anything about. The UI says the next sync will also be started by a
 * person, rather than implying a background job that will not exist.
 *
 * ── Incremental by high-watermark, not by change feed ──────────────────────────────────────
 *
 * `ingest()` already returns the greatest `updated_at` it saw, and the checkpoint already persists
 * it; a second run passes it as `since`. This inherits [ADR-016](../../../../docs/platform/11-technical-architecture.md#8--architecture-decision-records)'s
 * cursors-over-CDC posture rather than inventing a local variant — Salesforce CDC retains ~72 hours
 * and a PWA may be closed for weeks, so a change feed is the wrong primitive here for exactly the
 * reason it is wrong server-side.
 *
 * ⚠️ **A watermark cursor cannot see a delete.** That is not an oversight: [ADR-015](../../../../docs/platform/11-technical-architecture.md#8--architecture-decision-records)'s
 * reconciliation sweep is the correction, and `reconcile()` is already wired into Findings — so
 * local sync inherits a drift correction instead of needing to grow one.
 *
 * ── Every page goes through the relay ──────────────────────────────────────────────────────
 *
 * ADR-067 (0): the browser never holds a source token. The reader below calls `/api/relay/pull`,
 * which builds the URL from the registry and refuses anything it did not construct itself. The
 * relay returns **canonical** rows, which is why `canonical: true` is set — without it the
 * pipeline re-normalises already-normalised rows and silently loses every field whose provider name
 * differs from ours.
 */
import { ingest, type IngestPage, type IngestResult, type ProviderDescriptor,
  type IngestRecord, type ObjectDescriptor,
} from '@dedupr/connectors';
import type { LocalStore, StoredRecord, StoredSource, StoredSyncRun } from './store.js';

/** The per-file ceiling the PWA enforces. Deliberately low — see A-3 and `capacity.ts`. */
export const SYNC_RECORD_CAP = 50_000;

export interface SyncTarget {
  connectionId: string;
  /* The store's own union rather than a bare string: `StoredSource.provider` is closed, and a sync
     that produced a source row with an unknown provider would be unrenderable everywhere the UI
     maps a provider to a label. Widening it here would just move the failure.
     ⚠️ **`StoredSource['provider']` rather than a repetition of it** — this was a second copy of the
     same union and it went stale the moment a fifth provider existed, which is what the comment above
     was arguing against while doing it. */
  providerId: StoredSource['provider'];
  objectId: string;
  /** The `source` row these records belong to. One source per (connection, object). */
  sourceId: string;
  displayName: string;
}

export interface SyncOptions {
  target: SyncTarget;
  /** Bearer token for the relay. Fetched fresh per run so a stale one is refreshed, not refused. */
  accessToken: () => Promise<string | null>;
  /** Resume an interrupted run rather than starting over. Always an explicit choice — see below. */
  resume?: boolean;
  onProgress?: (written: number, page: number) => void;
  shouldStop?: () => boolean;
  now?: () => string;
  recordCap?: number;
}

export type SyncOutcome =
  | { ok: true; result: IngestResult }
  /** The relay refused. Its own code, because `not_connectable` and `rate_limited` differ entirely. */
  | { ok: false; code: string; detail: string };

/**
 * A page reader over the relay.
 *
 * ⚠️ Exported so a test can drive the pipeline without a network, which is the only way to exercise
 * the success path at all: nothing in the shipped registry is connectable
 * ([B-130](../../../../docs/platform/TODO.md)), so a reader bound to the real relay could only ever
 * assert the refusal — thorough-looking tests verifying nothing, which is the trap `relay.ts`'s own
 * header describes.
 */
export function relayPageReader(options: {
  target: SyncTarget;
  accessToken: () => Promise<string | null>;
  since?: string;
  onRefusal: (code: string, detail: string) => void;
}) {
  return async (cursor: string | undefined): Promise<IngestPage> => {
    const token = await options.accessToken();
    const response = await fetch('/api/relay/pull', {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        provider: options.target.providerId,
        object: options.target.objectId,
        connection: options.target.connectionId,
        since: options.since,
        cursor,
      }),
    });
    const payload = (await response.json().catch(() => null)) as Record<string, unknown> | null;
    if (!response.ok) {
      options.onRefusal(
        String(payload?.title ?? 'unavailable'),
        String(payload?.detail ?? `The pull was refused (${response.status}).`)
      );
      /* An empty last page rather than a throw: `ingest` checkpoints on the way out, so the run
         stops cleanly and what was already written stays. Throwing would lose the checkpoint and
         make a rate-limit into a restart-from-scratch. */
      return { rows: [], canonical: true };
    }
    return {
      /* ⚠️ **`records`, and it read `rows` — so every local pull from a real relay got nothing.**
         `handlePull` responds `{ records, cursor, max_results, quota_remaining }` and always has;
         the hosted caller reads it correctly. This read a key that is never present, so `ingest`
         saw an empty page with no cursor, concluded the pull was complete, and `runSync` recorded a
         **successful pull of zero records** — the "smaller answer presented as a complete one"
         failure this codebase has now hit five times in the ingest path.
         It survived because `sync.test.tsx` mocked pages as `{ rows: [...] }`: the test asserted
         the client against the client, and no provider is connectable yet, so nothing ever ran it
         against the real Worker. */
      rows: (payload?.records as Record<string, unknown>[]) ?? [],
      cursor: typeof payload?.cursor === 'string' ? payload.cursor : undefined,
      maxResults: typeof payload?.max_results === 'number' ? payload.max_results : undefined,
      /* ⚠️ The relay normalises before responding. Without this the pipeline looks the provider's
         own names up again and resolves `full_name` and the id to null on every row — no error, just
         fewer duplicates found. */
      canonical: true,
    };
  };
}

/**
 * `IngestRecord` → `StoredRecord`, **declared once**.
 *
 * ── Why this is a named function with a total key map ────────────────────────────────────────
 *
 * It was an inline object literal keeping eight of `IngestRecord`'s fields, and it silently dropped
 * every other one: `updated_at` (which the type marks *required*), `created_at`, `owner_pk`,
 * `active`, the five marketing signals and `extras`. So a document arrived on this device with a
 * filename and nothing else — and F07's audit over it could only ever have answered
 * `freshness: 'undated'` for 100% of them and `owner_state: 'unknown'` for 100% of them. The engine
 * would have been right; the pipeline had read the answer and thrown it away.
 *
 * ⚠️ **This is the FIFTH site of that defect in the ingest path**, and the previous four were each
 * fixed by extending a hand-written list — which is why a fifth existed. `INGESTED_FIELD_SPECS`
 * exists upstream precisely so a signal cannot be added and forgotten; the same discipline applies
 * here. `STORED_FROM_INGEST` is `Record<keyof …, …>`, so **omitting a key is a compile error**
 * rather than a field that quietly stops arriving.
 */
const STORED_FROM_INGEST = {
  /* Namespaced under the source, exactly as the CSV path does (Z-0). Two systems can hand back the
     same id, and the store is keyed on it — so without this a HubSpot `70001` would overwrite a
     Salesforce `70001`. */
  id: (r: IngestRecord) => `${r.source_id}:${r.id}`,
  source_id: (r: IngestRecord) => r.source_id,
  full_name: (r: IngestRecord) => r.full_name,
  email: (r: IngestRecord) => r.email,
  company: (r: IngestRecord) => r.company,
  phone: (r: IngestRecord) => r.phone,
  /* V-19b: what partitions the match run. Carried from the object descriptor via ingest, so a
     pulled Accounts source matches against companies, never people. */
  domain: (r: IngestRecord) => r.domain,
  kind: (r: IngestRecord) => r.kind,
  /* ⚠️ `|| null`, never a bare `Date.parse`: an unparseable date must become `null` rather than
     `NaN`, or an age computed from it renders as a real-looking number. */
  source_modified_at: (r: IngestRecord) => (r.updated_at ? Date.parse(r.updated_at) || null : null),
  created_at: (r: IngestRecord) => r.created_at ?? null,
  owner_pk: (r: IngestRecord) => r.owner_pk ?? null,
  active: (r: IngestRecord) => r.active ?? null,
} as const satisfies Record<keyof StoredRecord, (r: IngestRecord) => unknown>;

export function toStoredRecord(r: IngestRecord): StoredRecord {
  const out: Record<string, unknown> = {};
  for (const [key, read] of Object.entries(STORED_FROM_INGEST)) out[key] = read(r);
  /* ⚠️ The cast is plumbing, not a weakening: the guarantee lives on `STORED_FROM_INGEST`'s
     `satisfies Record<keyof StoredRecord, …>`, which is what makes a missing key a compile error.
     `Object.fromEntries` cannot express "these exact keys" to the type system, and pretending
     otherwise with a direct assertion is what the compiler correctly objected to. */
  return out as unknown as StoredRecord;
}

/**
 * The sink: this device's own store.
 *
 * ⚠️ `putRecords` is idempotent on `id` (an IndexedDB `put`), which the ingest contract *requires* —
 * a resumed run re-reads the page it was interrupted on, because a reader cannot know a page was
 * half-written, and an append-only sink would duplicate it on every resume.
 */
export function storeSink(store: LocalStore) {
  return {
    /* ⚠️ Typed as `IngestRecord` rather than restating its shape. The inline copy had to be widened by
       hand every time a kind was added (`document`, 2026-08-05) and would eventually disagree with the
       source of truth — a structural type that duplicates an exported one is a second definition
       waiting to drift. */
    async putRecords(records: IngestRecord[]) {
      await store.putRecords(records.map(toStoredRecord));
    },
    checkpoint: store.checkpoint.bind(store),
    readCheckpoint: store.readCheckpoint.bind(store),
  };
}

/**
 * Run one sync.
 *
 * ⚠️ **`resume` is the caller's explicit choice, never a default.** Silently continuing a pull
 * someone started last week — against an API quota that is *theirs* (BR-7) — is a decision to put
 * in front of them, not one to make on their behalf.
 */
export async function runSync(
  store: LocalStore,
  options: SyncOptions
): Promise<SyncOutcome> {
  const { target } = options;
  const now = options.now ?? (() => new Date().toISOString());
  const startedAt = now();

  /**
   * Record how this pull ended.
   *
   * ⚠️ **Written here, not in the screen.** A history the caller has to remember to append to is a
   * history that is complete for exactly one caller — the same shape as the audit chain that no
   * screen rendered and the ledger nothing called. Every path out of this function goes through it.
   */
  const record = async (
    outcome: StoredSyncRun['outcome'],
    reason: string,
    written: number,
    unblockable: number,
    detail?: string
  ) => {
    const finishedAt = now();
    try {
      await store.recordSyncRun({
        /* The source plus the finish time: two pulls of the same object are two rows, and a resume
           of one is its own row too, because it spent its own share of the customer's quota. */
        id: `${target.sourceId}@${finishedAt}`,
        source_id: target.sourceId,
        connection_id: target.connectionId,
        object_id: target.objectId,
        provider: target.providerId,
        started_at: startedAt,
        finished_at: finishedAt,
        written,
        outcome,
        reason,
        unblockable,
        resumed: options.resume === true,
        detail,
      });
    } catch {
      /* Swallowed deliberately: a history that could not be written must not turn a successful pull
         into a failed one. The records are already stored; losing the note about them is the lesser
         loss, and the checkpoint still carries the state that matters for correctness. */
    }
  };

  const provider = (await providersFor()).find((p) => p.id === target.providerId);
  if (!provider) {
    const detail = `No connector for ${target.providerId}.`;
    await record('refused', 'unknown_provider', 0, 0, detail);
    return { ok: false, code: 'unknown_provider', detail };
  }
  const object = provider.objects.find((o) => o.id === target.objectId);
  if (!object) {
    const detail = `${provider.displayName} has no ${target.objectId}.`;
    await record('refused', 'unknown_object', 0, 0, detail);
    return { ok: false, code: 'unknown_object', detail };
  }
  /**
   * ⚠️ **Refuse rather than pull with an unknown kind — [B-308](../../../../docs/platform/TODO.md).**
   *
   * `ingest()` stamps `object.kind` on every record; `isBlockable` resolves what identifies that
   * sort of thing from it, and the engine partitions match runs on it. With no kind every record is
   * counted `unblockable` and none is ever compared — which reaches a person as *"N records · N
   * unmatchable"* beside *"nothing looked like a duplicate"*. Both sentences are about **our**
   * projection and both read as facts about **their** CRM.
   *
   * ⚠️ **A refusal here rather than a louder count, because the count already WAS the louder answer
   * and it was not loud enough.** `isBlockable` returns false for an unrecognised kind by design —
   * *"the noisier of the two answers and the right one"* — but noise that renders as a normal,
   * plausible statistic is indistinguishable from a finding. This refuses before spending the
   * customer's API quota (BR-7) on a pull whose result could only mislead.
   */
  if (!object.kind) {
    const detail =
      `Dedupr does not know what sort of thing ${provider.displayName} ${target.objectId} is, so it ` +
      `cannot tell what identifies one. Nothing was pulled — a pull without that would report every ` +
      `record as uncheckable and no duplicates, which is not the same as finding none.`;
    await record('refused', 'unknown_object_kind', 0, 0, detail);
    return { ok: false, code: 'unknown_object_kind', detail };
  }

  /* The previous run's watermark, and ONLY when this is not a resume: a resume continues the same
     pass and `ingest` restores its own `since` from the checkpoint's cursor. Passing a watermark
     into a resume would re-window mid-run and skip whatever changed in between. */
  const previous = await store.readCheckpoint(target.sourceId);
  const since = options.resume ? undefined : previous?.highWatermark ?? undefined;

  let refusal: { code: string; detail: string } | null = null;

  /**
   * ⚠️ **The re-window loop (V-11), and the reason `hit_source_ceiling` is not the end of a pull.**
   *
   * HubSpot's search API returns nothing past ~10,000 results however you page it, so `ingest` stops
   * there rather than paging into silence. Until 2026-08-04 that was the *whole* story: the run ended,
   * the screen said "partial", and **a portal with more than 10,000 changed contacts could never be
   * fully synced** — the descriptor's own comment prescribed windowing by `hs_lastmodifieddate` and
   * nothing implemented it.
   *
   * So a ceiling starts a new window at the watermark the last one reached. Three things make that
   * safe rather than merely plausible:
   *
   * - **The filter is `GTE`, not `GT`.** A record whose timestamp equals the watermark is read again
   *   rather than skipped, and `putRecords` is idempotent on id — so the overlap costs a re-read and
   *   never a gap. Correctness first; a wasted page is the cheap direction to be wrong in.
   * - **The cursor is cleared between windows.** A new window is a new query; resuming the old
   *   cursor would page straight back into the empty region past the cap.
   * - **It stops when it cannot progress.** If a window returns a watermark no later than the one it
   *   started from — >10,000 records sharing a single timestamp — re-windowing cannot help, and
   *   looping would spend the customer's quota (BR-7) re-reading the same page for ever. That case
   *   ends the pull honestly instead of pretending, and it is reported distinctly, because the remedy
   *   is different from every other partial outcome.
   */
  const MAX_WINDOWS = 20;
  let windows = 0;
  let sinceForWindow = since;
  /* Tracked here rather than read back from the checkpoint: a final window that happens to read zero
     rows reports `highWatermark: null`, and letting that land would send the next run back to a full
     pull. The maximum across windows is what a completed pass actually justifies. */
  let bestWatermark: string | null = previous?.highWatermark ?? null;
  let totalWritten = 0;
  let totalUnblockable = 0;
  let stalled = false;
  let result: IngestResult;

  const cap = options.recordCap ?? SYNC_RECORD_CAP;

  for (;;) {
    windows += 1;
    result = await ingest({
      sourceId: target.sourceId,
      provider,
      object,
      read: relayPageReader({
        target,
        accessToken: options.accessToken,
        since: sinceForWindow,
        onRefusal: (code, detail) => {
          refusal = { code, detail };
        },
      }),
      sink: storeSink(store),
      /* ⚠️ **The REMAINING cap, not the whole cap.** `ingest` counts `written` per pass and resets it
         for a non-resumed one, so passing the full cap to every window would let 20 windows write
         20 × 50,000 records — straight past `MAX_RECORDS`, which is the one limit this tier's whole
         memory story rests on. The cap is a budget for the pull, not an allowance per query. */
      recordCap: cap - totalWritten,
      /* Only the FIRST window may resume: the later ones are new queries by construction, and
         resuming one would restore the previous window's cursor. */
      resume: windows === 1 ? options.resume : false,
      /* Cumulative, for the same reason: `ingest` reports this window's own count, and a progress
         figure that restarted at zero on every re-window would read as the pull going backwards. */
      onProgress: options.onProgress
        ? (written, page) => options.onProgress!(totalWritten + written, page)
        : undefined,
      shouldStop: () => options.shouldStop?.() === true || refusal !== null,
    });

    totalWritten += result.written;
    totalUnblockable += result.unblockable;
    if (result.highWatermark && (bestWatermark === null || result.highWatermark > bestWatermark)) {
      bestWatermark = result.highWatermark;
    }

    if (refusal || result.reason !== 'hit_source_ceiling') break;
    if (windows >= MAX_WINDOWS) break;
    /* The pull's own record budget is spent. Opening another window would either write nothing or
       exceed the cap, and `hit_record_cap` is the outcome that belongs on the run either way. */
    if (totalWritten >= cap) break;

    /* No later watermark than we started this window with: every remaining record shares one
       timestamp, so the next window would return the same page. Not a failure of the sync — a limit
       of what a timestamp filter can express — and it must not become a loop. */
    if (!result.highWatermark || (sinceForWindow !== undefined && result.highWatermark <= sinceForWindow)) {
      stalled = true;
      break;
    }

    sinceForWindow = result.highWatermark;
    /* A fresh query: no cursor, and `written` reset because the next window counts its own. The
       watermark is kept so an interruption between windows still resumes sensibly. */
    await store.checkpoint(target.sourceId, {
      cursor: undefined,
      written: 0,
      highWatermark: bestWatermark,
      done: false,
    });
  }

  /* One pull, from the caller's point of view: the windows are an implementation detail of getting
     past a provider's cap, so the totals are the pull's totals. */
  result = { ...result, written: totalWritten, unblockable: totalUnblockable, highWatermark: bestWatermark };

  if (refusal) {
    /* ⚠️ **A refused run must not be checkpointed as complete, and it must not advance the
       watermark. Both were wrong in the first version, and the second is the dangerous one.**
       The reader turns a refusal into an empty last page so the run stops cleanly and keeps what it
       wrote — but `ingest` then sees no cursor, concludes `complete`, and records `done: true` with
       the highest `updated_at` it happened to see.
       That is a silent data-loss bug. Rows do not arrive in timestamp order, so a partial pull's
       maximum is **not** a safe floor: the next incremental run would send it as `since` and skip
       every record changed before it that the refused pull never reached. Nothing would error, and
       the duplicate count would simply be computed over less data than it claimed.
       So the run is re-checkpointed as unfinished, keeping the cursor to resume from, and the
       watermark is rolled back to the last one a COMPLETED pass justified. Re-reading is cheap;
       skipping is unrecoverable. */
    const cut = await store.readCheckpoint(target.sourceId);
    await store.checkpoint(target.sourceId, {
      cursor: cut?.cursor,
      written: cut?.written ?? result.written,
      highWatermark: previous?.highWatermark ?? null,
      done: false,
    });
    const refused = refusal as { code: string; detail: string };
    await record('refused', refused.code, result.written, result.unblockable, refused.detail);
    return { ok: false, ...refused };
  }

  await store.addSource(
    {
      id: target.sourceId,
      provider: target.providerId,
      display_name: target.displayName,
      row_count: result.written,
      ingested_at: now(),
      /* Carried onto the source so a merge can later name the record it came from. Without this a
         writeback has no target and the executor's second run target is unreachable from any
         screen. */
      connection_id: target.connectionId,
      object_id: target.objectId,
    },
    []
  );

  /* ⚠️ The watermark that the whole pull justifies, written after the loop rather than left to the
     last window. `ingest` checkpoints per pass, and a final window that read zero rows reports a null
     watermark — letting that stand would send the next run back to a full pull. */
  if (result.reason === 'complete' && bestWatermark) {
    await store.checkpoint(target.sourceId, {
      cursor: undefined,
      written: result.written,
      highWatermark: bestWatermark,
      done: true,
    });
  }

  /* The re-window loop's own outcomes, named apart from an ordinary partial because each needs a
     different response from whoever reads it:
       - `stalled_on_timestamp` — more than the provider's cap share one timestamp, so no window can
         advance. Re-running changes nothing; the remedy is a narrower object or a full re-read.
       - `window_limit` — we stopped ourselves after MAX_WINDOWS rather than paging indefinitely
         against the customer's quota. Re-running continues from the watermark reached.
     Both are honest `partial`s. Neither is "failed", and neither is silently "complete". */
  const reason = stalled
    ? 'stalled_on_timestamp'
    : result.reason === 'hit_source_ceiling' && windows >= MAX_WINDOWS
      ? 'window_limit'
      : result.reason;

  await record(
    result.reason === 'complete' ? 'complete' : 'partial',
    reason,
    result.written,
    result.unblockable,
    windows > 1 ? `Read in ${windows} windows to get past the provider's result cap.` : undefined
  );
  return { ok: true, result };
}

/**
 * The provider registry, from the relay.
 *
 * Fetched rather than imported so a browser build does not carry a second copy that can drift from
 * the deployment's own — the descriptors are data, and the relay is where they live.
 */
/**
 * The exact shape `handleProviders` (`apps/web/worker/relay.ts`) puts on the wire — snake_case,
 * and a deliberately reduced projection of the real `ObjectDescriptor`/`ProviderDescriptor` (no
 * pull/discovery/auth specs; the browser never needs them, since a local-mode pull is proxied
 * through the relay rather than built client-side).
 *
 * ⚠️ **This existed as an unchecked `as { providers?: ProviderDescriptor[] }` cast until found
 * live, 2026-08-15** — the response was asserted to already be the full, camelCase internal type,
 * so `object.displayName` read `undefined` for every provider and every object. The dropdown
 * rendered with real `<option>` values and no visible label at all: not empty by state, empty by
 * appearance — reported live while testing the first real HubSpot pull this product has driven.
 * `tsc` could not have caught it: `as` overrides the checker entirely, and both branches of the
 * cast (a snake_case wire shape vs. the sprawling internal descriptor) reduce to the same `any`
 * the moment the cast is written.
 */
interface RelayObjectSummary {
  id: string;
  display_name: string;
  /**
   * ⚠️ **REQUIRED, and its absence was [B-308](../../../../docs/platform/TODO.md).**
   *
   * `ingest()` reads exactly one field off a descriptor — `object.kind` — and stamps it on every
   * record. `isBlockable` looks up `IDENTIFYING_FIELDS[kind]` to decide what identifies this sort of
   * thing, and an unrecognised kind answers **false**: deliberately the noisier direction, on the
   * reasoning that a bug should be visible. It was not visible. It rendered as *"2 records · 2
   * unmatchable"*, which reads as a fact about the customer's CRM rather than about our projection.
   *
   * Declared `required` rather than optional so the mapping below cannot compile without it.
   */
  kind: ObjectDescriptor['kind'];
}
interface RelayProviderSummary {
  id: string;
  display_name: string;
  connectable: boolean;
  objects: RelayObjectSummary[];
}

let cachedProviders: ProviderDescriptor[] | null = null;
export async function providersFor(): Promise<ProviderDescriptor[]> {
  if (cachedProviders) return cachedProviders;
  try {
    const response = await fetch('/api/relay/providers');
    const payload = (await response.json()) as { providers?: RelayProviderSummary[] };
    /* Mapped into the internal shape at the one seam that reads the wire, rather than teaching
       every consumer (already written against `ProviderDescriptor`'s camelCase contract) to read
       snake_case. Fields the wire never sends stay absent — nothing here reads them; adding one
       that does must extend this mapping, not another unchecked cast further down. */
    cachedProviders = (payload.providers ?? []).map(
      (p) =>
        ({
          id: p.id,
          displayName: p.display_name,
          connectable: p.connectable,
          objects: p.objects.map((o) => ({ id: o.id, displayName: o.display_name, kind: o.kind })),
        }) as unknown as ProviderDescriptor
    );
  } catch {
    /* Empty rather than a throw: the caller turns "no such provider" into a readable refusal, and a
       network failure here must not take a screen down. */
    cachedProviders = [];
  }
  return cachedProviders;
}

/** For tests, and for a deployment whose registry changed under a long-lived tab. */
export function resetProviderCache(providers: ProviderDescriptor[] | null = null): void {
  cachedProviders = providers;
}
